const { CartLineDetailsTabNameEnum } = require('@/epos/vue/modals/CartLineDetails/CartLineDetailsTabNameEnum');
const { MODAL_NAMES__EPOS } = require('@/epos/vue/modals/MODAL_NAMES__EPOS');

/**
 * @param $timeout
 * @param {CartService} cartService
 * @param {DialogService} dialogService
 * @param {VueModalFactory} vueModalFactory
 */
const cartItemQuantityListenerDirective = function (
    $timeout,
    cartService,
    dialogService,
    vueModalFactory
) {
    return {
        /**
         * @param {{cartItem:CartLine, displayNewMembershipDatasetModal:function(CartLine)}} scope
         * @param element
         * @param {{dtCartItemQuantityListener:("manual"|"minus"|"plus")}} attrs
         */
        link(scope, element, attrs) {
            /**
             * @return {*|promise|{then, catch, finally}|*}
             */
            function invokeReturnReasonModal() {
                const props = {
                    cartLine: scope.cartItem,
                    initialTabName: CartLineDetailsTabNameEnum.RETURNS,
                    onlyReturns: true,
                    cancelLabel: 'SELL.CANCEL_RETURNING',
                };

                return vueModalFactory
                    .open(
                        MODAL_NAMES__EPOS.CART_LINE_DETAILS,
                        props
                    );
            }

            /**
             * Display the return reason picker (and maybe supervisor approval)
             * when first changing an item's quantity to negative.
             *
             * @param {number} adjustBy
             *
             * @return {Promise}
             */
            async function handleReturn(adjustBy) {
                return invokeReturnReasonModal().then(
                    function () {
                        return cartService.adjustCartLineQuantity(scope.cartItem, adjustBy);
                    },
                    function () {
                        // Update the qty displayed.
                        return cartService.adjustCartLineQuantity(scope.cartItem, 0);
                    }
                );
            }

            /**
             * Process a change in quantity.
             *
             * @param {Event} event
             *
             * NOTE: Only use adjustQuantity() to make the changes to the quantity.
             */
            async function handleQuantityChanged(event) {
                element.disabled = true;
                event.preventDefault();
                event.stopPropagation();

                let newQuantity;
                let currentQuantity = scope.cartItem.getQuantity();

                switch (attrs.dtCartItemQuantityListener) {
                    case 'plus':
                        if ((currentQuantity + 1) >= 1000) {
                            // Disallow quantities over 999.
                            return;
                        }
                        newQuantity = currentQuantity + 1;
                        break;
                    case 'minus':
                        if ((currentQuantity - 1) <= -1000) {
                            // Disallow quantities under -999.
                            return;
                        }
                        newQuantity = currentQuantity - 1;
                        break;
                    case 'manual':
                        if (!scope.cartItem.validateQuantityChange()) {
                            $timeout(() => {
                                scope.cartItem.quantityDisplay = currentQuantity;
                            });
                            return;
                        }
                        newQuantity = scope.cartItem.quantityDisplay || 0;
                        break;
                }

                let orderState = scope.cart.currentCart.isEditingOrder ? ' - editing order' : '';

                switch (scope.cartItem.getItemType() + ' - ' + attrs.dtCartItemQuantityListener + orderState) {
                    // Force Order Balance quantity to always be one, regardless of any attempts to modify it.
                    case 'Order Balance - plus':
                    case 'Order Balance - minus':
                    case 'Order Balance - manual':
                        await cartService.adjustCartLineQuantity(scope.cartItem, 0);
                        break;

                        // Membership plans cannot be refunded, so no -ve quantities.
                        // Always ask to confirm the removal of a membership.
                    case 'Membership Plan - plus':
                        await scope.displayNewMembershipDatasetModal(scope.cartItem)
                            .then(async (membershipDatasets) => {
                                await cartService.adjustCartLineQuantity(scope.cartItem, 1);
                                await cartService.modifyCurrentCart(() => {
                                    scope.cartItem.setMembershipDatasets(membershipDatasets);
                                });
                            })
                            .catch(() => {
                                console.warn('Membership data entry cancelled. Cancelling qty increase.');
                            });
                        break;
                    case 'Membership Plan - minus':
                        dialogService
                            .confirm('MEMBERSHIPS.CONFIRM_REMOVE')
                            .then(async (confirmed) => {
                                if (confirmed) {
                                    await cartService.adjustCartLineQuantity(scope.cartItem, -1);
                                }
                            });
                        break;
                    case 'Membership Plan - manual':
                        // Should be disabled in the UI as only the + and - buttons should work for membership plans.
                        await cartService.adjustCartLineQuantity(scope.cartItem, 0);
                        break;

                        // Tickets and Products require a return reason for -ve quantities.
                    case 'Ticket - plus':
                    case 'Product - plus':
                        await cartService.adjustCartLineQuantity(scope.cartItem, 1);
                        break;
                    case 'Ticket - minus':
                    case 'Product - minus':
                        if (currentQuantity === 1) {
                            // Current quantity is 1, so adjust quantity by -2 to
                            // go from +1 to -1.
                            await handleReturn(-2);
                        } else {
                            await cartService.adjustCartLineQuantity(scope.cartItem, -1);
                        }
                        break;
                    case 'Ticket - minus - editing order':
                    case 'Product - minus - editing order':
                        // In the context of editing an order let's not do refunds. If you want to refund an item as
                        // opposed to editing it do a proper refund.
                        // The minimum quantity is 0 which simply means all instances of this item have been deleted.
                        if (currentQuantity <= 0) {
                            await cartService.adjustCartLineQuantity(scope.cartItem, 0);
                        } else {
                            await cartService.adjustCartLineQuantity(scope.cartItem, -1, false);
                        }
                        break;
                    case 'Ticket - manual':
                    case 'Product - manual':
                        if (newQuantity === 0) {
                            newQuantity -= currentQuantity;
                        }
                        if (newQuantity < 0 && scope.cartItem.returnReason.reasonID === null) {
                            await handleReturn(newQuantity - currentQuantity);
                        } else {
                            await cartService.adjustCartLineQuantity(scope.cartItem, newQuantity - currentQuantity);
                        }
                        break;
                    case 'Ticket - manual - editing order':
                    case 'Product - manual - editing order':
                        // In the context of editing an order let's not do refunds. If you want to refund an item as
                        // opposed to editing it do a proper refund.
                        // The minimum quantity is 0 which simply means all instances of this item have been deleted.
                        // Note: you can't edit a product.
                        if (newQuantity <= 0) {
                            await cartService.adjustCartLineQuantity(scope.cartItem, -currentQuantity);
                        } else {
                            await cartService.adjustCartLineQuantity(scope.cartItem, newQuantity - currentQuantity);
                        }
                        break;

                        // Generic item type. We just adjust quantity as necessary. No return reasons.
                    default:
                        if (newQuantity === 0 && newQuantity > currentQuantity) {
                            // Increasing but we don't want to stop at zero so go to 1.
                            newQuantity = 1;
                        } else if (newQuantity === 0 && newQuantity < currentQuantity) {
                            // Decreasing but we don't want to stop at zero so go to -1.
                            newQuantity = -1;
                        }

                        await cartService.adjustCartLineQuantity(scope.cartItem, newQuantity - currentQuantity);
                }

                if (newQuantity !== currentQuantity) {
                    cartService.checkProductAvailability(scope.cartItem);
                }

                element.disabled = false;

                // Calculate the cart again to check for any new stock level errors.
                $timeout(() => {
                    cartService.calculate();
                });

                scope.$apply();
            }

            // Bind to appropriate event for the type of element.
            element.on({ INPUT: 'change', BUTTON: 'click' }[element[0].nodeName], function (event) {
                handleQuantityChanged(event);
            });
        },
    };
};

module.exports = cartItemQuantityListenerDirective;
